<script lang="ts">
  /* «Динамика»: every dashboard figure along a horizontal time line — one bar
     per period, the plan as a dash across it. Count and amount have their own
     scales, so a pair block draws two charts. */
  import { _ } from "svelte-i18n";
  import LoadErrorState from "$components/LoadErrorState.svelte";
  import { runLoad, type LoadStatus } from "$lib/loadState";
  import {
    BLOCKS,
    bucketLabel,
    fetchDashboardSeries,
    formatFigure,
    type DashboardSeries,
    type Metric,
    type Period,
  } from "$lib/dashboard";

  let {
    slug,
    period,
    anchor,
    reloadKey = 0,
  }: { slug: string; period: Period; anchor: string; reloadKey?: number } = $props();

  let series = $state<DashboardSeries | null>(null);
  let status = $state<LoadStatus>("loading");
  let requestId = 0;

  function load() {
    const id = ++requestId;
    runLoad(
      (s) => {
        if (id === requestId) status = s;
      },
      async () => {
        const res = await fetchDashboardSeries(slug, period, anchor);
        if (id === requestId) series = res;
      },
    );
  }

  $effect(() => {
    void [slug, period, anchor, reloadKey];
    load();
  });

  const SLOT = 48;
  const HEIGHT = 150;
  const TOP = 18;
  const BOTTOM = 22;

  function scaleOf(metric: Metric): number {
    let max = 0;
    for (const b of series?.buckets ?? []) {
      max = Math.max(max, b.values[metric] ?? 0, b.goals[metric] ?? 0);
    }
    return max || 1;
  }

  function y(value: number, max: number): number {
    return TOP + (HEIGHT - TOP - BOTTOM) * (1 - value / max);
  }

  /** The newest period sits on the right: start a long line scrolled there. */
  function scrollToEnd(node: HTMLElement) {
    node.scrollLeft = node.scrollWidth;
  }
</script>

{#if status === "error"}
  <LoadErrorState onretry={load} />
{:else if series}
  <div class="dyn-legend">
    <span class="legend-item"><span class="legend-bar"></span>{$_("dashboard.chart.actual")}</span>
    <span class="legend-item"><span class="legend-goal"></span>{$_("dashboard.chart.goal")}</span>
    <span class="legend-item"><span class="legend-bar legend-bar--met"></span>{$_("dashboard.chart.met")}</span>
  </div>
  <div class="dyn-grid">
    {#each BLOCKS as block (block.id)}
      <div class="card dyn-card">
        <h3 class="dyn-title">
          <span class="material-icons-outlined" aria-hidden="true">{block.icon}</span>
          {$_(block.labelKey)}
        </h3>
        {#each block.parts as part (part.metric)}
          {@const max = scaleOf(part.metric)}
          {@const width = series.buckets.length * SLOT}
          {#if block.parts.length > 1}<div class="dyn-part">{$_(part.partKey)}</div>{/if}
          <div class="chart-scroll" use:scrollToEnd>
            <svg {width} height={HEIGHT} viewBox="0 0 {width} {HEIGHT}" role="img" aria-label={$_(block.labelKey)}>
              <line class="axis" x1="0" x2={width} y1={HEIGHT - BOTTOM} y2={HEIGHT - BOTTOM} />
              {#each series.buckets as bucket, i (bucket.start)}
                {@const value = bucket.values[part.metric] ?? 0}
                {@const goal = bucket.goals[part.metric]}
                {@const x = i * SLOT}
                {@const label = bucketLabel(series.granularity, bucket.start, $_)}
                <g>
                  <title>{label}: {formatFigure(part.metric, value)}{goal != null ? ` · ${$_("dashboard.chart.goal")} ${formatFigure(part.metric, goal)}` : ""}</title>
                  <rect
                    class="bar"
                    class:bar--met={goal != null && goal > 0 && value >= goal}
                    x={x + SLOT * 0.2}
                    y={y(value, max)}
                    width={SLOT * 0.6}
                    height={Math.max(0, HEIGHT - BOTTOM - y(value, max))}
                    rx="2"
                  />
                  {#if goal != null && goal > 0}
                    <line class="goal" x1={x + SLOT * 0.08} x2={x + SLOT * 0.92} y1={y(goal, max)} y2={y(goal, max)} />
                  {/if}
                  {#if value > 0}
                    <text class="val" x={x + SLOT / 2} y={y(value, max) - 4}>{formatFigure(part.metric, value, true)}</text>
                  {/if}
                  <text class="tick" x={x + SLOT / 2} y={HEIGHT - 6}>{label}</text>
                </g>
              {/each}
            </svg>
          </div>
        {/each}
      </div>
    {/each}
  </div>
{:else}
  <p class="dyn-loading">{$_("common.loading")}</p>
{/if}

<style>
  .dyn-legend { display: flex; gap: 18px; flex-wrap: wrap; font-size: 12px; color: var(--color-text-muted); }
  .legend-item { display: inline-flex; align-items: center; gap: 6px; }
  .legend-bar { width: 12px; height: 12px; border-radius: 2px; background: var(--color-primary); }
  .legend-bar--met { background: #2e7d32; }
  .legend-goal { width: 16px; border-top: 2px dashed #e65100; }

  .dyn-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
  @media (max-width: 1000px) { .dyn-grid { grid-template-columns: minmax(0, 1fr); } }

  .dyn-card { padding: 18px 20px; display: flex; flex-direction: column; gap: 8px; min-width: 0; }
  .dyn-title {
    display: flex; align-items: center; gap: 8px; margin: 0;
    font-size: 15px; font-weight: 600; color: var(--color-surface-darker);
  }
  .dyn-title .material-icons-outlined { font-size: 20px; color: var(--color-primary); }
  .dyn-part { font-size: 12px; font-weight: 600; color: var(--color-text-muted); text-transform: uppercase; letter-spacing: 0.04em; }

  .chart-scroll { overflow-x: auto; }
  svg { display: block; }
  .axis { stroke: var(--color-divider); stroke-width: 1; }
  .bar { fill: var(--color-primary); }
  .bar--met { fill: #2e7d32; }
  .goal { stroke: #e65100; stroke-width: 2; stroke-dasharray: 4 3; }
  .val { font-size: 10px; fill: var(--color-surface-darker); text-anchor: middle; font-weight: 600; }
  .tick { font-size: 10px; fill: var(--color-text-muted); text-anchor: middle; }
  .dyn-loading { margin: 0; font-size: 13px; color: var(--color-text-muted); }
</style>
